import { type IHttpClient } from '@wix/yoshi-flow-editor';
import {
  getPostFeedPage,
  getPostFeedMetadataPage,
} from '@wix/ambassador-blog-frontend-adapter-public-v2-post-feed-page/http';
import { FeedType } from '@wix/ambassador-blog-frontend-adapter-public-v2-post-feed-page/types';
import type { Post } from '@wix/ambassador-blog-frontend-adapter-public-v2-post-page/types';
import type { ErrorHandlerPublicAPI } from '@wix/yoshi-flow-editor/build/cjs/exports/error-handler';
import {
  BLOG_HEADER_TOTAL_RESULTS,
  EXPERIMENTS,
  getWixDataCategoryId,
  getWixDataTagId,
  handleAggregatorResponseWithHeaders,
  isExperimentEnabled,
  postListWidgetStyleParams as params,
} from '@wix/communities-blog-client-common';
import { WarmupDataHandler } from '@app/common/warmup-data-handler';
import { setPostListMetadataParams } from '@app/external/common/actions/fetch-post-list-metadata';
import { fetchProfileUrls } from '@app/external/common/store/profile-urls/profile-urls-actions';
import setPosts from '../../common/actions/set-posts';
import { createPromisifiedAction } from '../../common/actions-promisifier/create-promisified-action';
import { getAppSettingsValue } from '../../common/selectors/app-settings-base-selectors';
import { getQueryLocale } from '../../common/selectors/locale-selectors';
import { getDemoPosts } from '../../common/services/demo-posts';
import getEnvironment from '../../common/services/get-environment';
import { getTotalResults } from '../../common/services/pagination';
import { normalizePostV3 } from '../../common/services/post-utils';
import { fetchCategoriesSuccess } from '../../common/store/categories/fetch-categories';
import { getInstanceId } from '../../common/store/instance-values/instance-values-selectors';
import { setIsLoading } from '../../common/store/is-loading/is-loading-actions';
import { setPostCount } from '../../common/store/post-count/set-posts-count';
import { fetchTopology } from '../../common/store/topology/topology-actions';
import { getPostListWidgetPageSize } from '../selectors/post-list-widget-page-size';
import { type PostListWidgetThunkAction } from '../types/post-list-widget-thunk-args';

type FetchPostListFrontendAdapterParams = {
  page?: number;
  httpClient: IHttpClient;
  includeInitialPageData: boolean;
  featuredOnly: boolean;
  categoryId: string;
  tagId: string;
  language: string;
  pageSize: number;
  errorHandler: ErrorHandlerPublicAPI;
  isSSR: boolean;
};

const fetchPostListFrontendAdapter = async ({
  page = 1,
  httpClient,
  includeInitialPageData,
  featuredOnly = false,
  categoryId,
  tagId,
  language,
  pageSize,
  errorHandler,
  isSSR,
}: FetchPostListFrontendAdapterParams) => {
  const { withErrorHandler, getResolvedError } = errorHandler;

  const fetchMetadata = () =>
    withErrorHandler(
      () =>
        httpClient.request(
          getPostFeedMetadataPage({
            type: FeedType.POST_LIST_WIDGET,
            page,
            pageSize,
            languageCode: language,
            postListWidgetOptions: {
              featuredOnly,
              categoryId,
              tagId,
            },
          }),
        ),
      { errorCodesMap: { applicationError: { UNAUTHENTICATED: false } } },
    ).catch((error) => {
      getResolvedError(error);
      throw error;
    });

  const [response, metadataResponse] = await Promise.all([
    withErrorHandler(
      () =>
        httpClient.request(
          getPostFeedPage({
            languageCode: language,
            page,
            pageSize,
            includeInitialPageData,
            type: FeedType.POST_LIST_WIDGET,
            postListWidgetOptions: {
              featuredOnly,
              categoryId,
              tagId,
            },
            translationsName: 'post-list-widget',
          }),
        ),
      {
        errorCodesMap: {
          applicationError: { UNAUTHENTICATED: false, NOT_FOUND: false, PERMISSION_DENIED: false },
        },
      },
    ).catch((error) => {
      getResolvedError(error);
      throw error;
    }),
    isSSR
      ? Promise.resolve({
          data: {
            postFeedMetadataPage: {
              postMetrics: {} as any,
              postLikes: {} as any,
            },
          },
        })
      : fetchMetadata(),
  ]).catch((error) => {
    getResolvedError(error);
    throw error;
  });

  return { response, metadataResponse };
};

const handlePostListPostsResponse =
  (postsResponse: any, { page = 1 } = {}): PostListWidgetThunkAction =>
  (dispatch, getState, { platformApi, httpClient, aggregatorRequest, errorHandler }) => {
    dispatch(setIsLoading('postListPosts', undefined, true));

    return dispatch(handleAggregatorResponseWithHeaders(postsResponse))
      .then(async ({ body, headers }) => {
        if (!body.length && getEnvironment(platformApi).isEditorSegment) {
          const state = getState();
          const fake = await getDemoPosts({
            httpClient,
            aggregatorRequest,
            getState,
            dispatch,
            platformApi,
            query: {
              page,
              pageSize: getPostListWidgetPageSize(state),
              featuredOnly: getAppSettingsValue({
                state,
                key: `style.booleans.${params.postListWidgetIsFeatured.key}`,
              }),
              categoryId: getWixDataCategoryId(state),
              tagId: getWixDataTagId(state),
              language: getQueryLocale(state),
            },
            errorHandler,
          });

          body = fake.posts;
          headers = fake.headers as any;
        }

        dispatch(setPosts(body));
        dispatch(setPostCount(getTotalResults(headers)));
      })
      .then(() => dispatch(setIsLoading('postListPosts', undefined, false)))
      .catch(() => dispatch(setIsLoading('postListPosts', undefined, false)));
  };

export const fetchInitialData =
  (): PostListWidgetThunkAction =>
  async (dispatch, getState, { compId, flowAPI, platformApi, httpClient, errorHandler }) => {
    const state = getState();
    const instanceId = getInstanceId(state);
    const featuredOnly = getAppSettingsValue({
      state,
      key: `style.booleans.${params.postListWidgetIsFeatured.key}`,
    });
    const categoryId = getWixDataCategoryId(state) || undefined;
    const tagId = getWixDataTagId(state) || undefined;
    const language = getQueryLocale(state) ?? platformApi.site.language;
    const pageSize = getPostListWidgetPageSize(state);
    const isSSR = flowAPI.environment.isSSR;

    dispatch(
      setPostListMetadataParams({
        type: FeedType.POST_LIST_WIDGET,
        page: 1,
        pageSize,
        languageCode: language,
        postListWidgetOptions: {
          featuredOnly: featuredOnly ?? false,
          categoryId,
          tagId,
        },
      }),
    );

    const data = await new WarmupDataHandler({
      platformApi,
      warmupDataKey: `post-list-${compId}-${featuredOnly}-${categoryId}-${tagId}-${language}-${pageSize}`,
      isSSR,
      isWarmupDataExperimentEnabled: isExperimentEnabled(
        state,
        EXPERIMENTS.USE_WARMUP_STATE_IN_POST_LIST,
      ),
      loaderFn: () =>
        fetchPostListFrontendAdapter({
          page: 1,
          httpClient,
          includeInitialPageData: true,
          categoryId,
          tagId,
          language,
          pageSize,
          featuredOnly,
          errorHandler,
          isSSR,
        }),
    }).load();

    const postFeedPage = data.response.data.postFeedPage;
    const postFeedMetadataPage = data.metadataResponse?.data?.postFeedMetadataPage;

    if (postFeedPage?.categories) {
      dispatch(fetchCategoriesSuccess(postFeedPage?.categories));
    }

    dispatch(fetchTopology(instanceId));
    await dispatch(
      handlePostListPostsResponse({
        status: 200,
        body: postFeedPage?.posts?.posts.map((post: Post) => {
          const metrics = postFeedMetadataPage?.postMetrics?.[post.id!];

          return normalizePostV3({
            ...post,
            metrics,
          });
        }),
        headers: {
          [BLOG_HEADER_TOTAL_RESULTS]: postFeedPage?.posts?.pagingMetaData?.total,
        },
      }),
    );
    dispatch(fetchProfileUrls());
  };

export const fetchPostListPosts =
  (page?: number): PostListWidgetThunkAction =>
  async (dispatch, getState, { httpClient, platformApi, errorHandler, flowAPI }) => {
    const state = getState();

    const featuredOnly = getAppSettingsValue({
      state,
      key: `style.booleans.${params.postListWidgetIsFeatured.key}`,
    });
    const categoryId = getWixDataCategoryId(state) || undefined;
    const tagId = getWixDataTagId(state) || undefined;
    const language = getQueryLocale(state) ?? platformApi.site.language;
    const pageSize = getPostListWidgetPageSize(state);
    const isSSR = flowAPI.environment.isSSR;

    const data = await fetchPostListFrontendAdapter({
      page,
      httpClient,
      includeInitialPageData: false,
      featuredOnly,
      categoryId,
      tagId,
      language,
      pageSize,
      errorHandler,
      isSSR,
    });

    const postFeedPage = data.response.data.postFeedPage;
    const postFeedMetadataPage = data?.metadataResponse?.data?.postFeedMetadataPage;

    await dispatch(
      handlePostListPostsResponse(
        {
          status: 200,
          body: postFeedPage?.posts?.posts?.map((post: Post) =>
            normalizePostV3({
              ...post,
              metrics: postFeedMetadataPage?.postMetrics?.[post.id!],
            }),
          ),
          headers: {
            [BLOG_HEADER_TOTAL_RESULTS]: postFeedPage?.posts?.pagingMetaData?.total,
          },
        },
        { page },
      ),
    );
  };

export const fetchPostListPostsPromisified = createPromisifiedAction(
  fetchPostListPosts,
  () => null,
  (response) => response.status,
);
